import { useQuery } from '@apollo/client';
// Api
import { GET_ARTICLES_BY_HASHTAG } from 'api/articles/queries';
// Types
import {
  GetArticlesByHashtag,
  GetArticlesByHashtagVariables,
} from 'api/articles/types/GetArticlesByHashtag';
import { SortDirection } from 'api/graphql-global-types';
// Ui
import ContentCard, { ContentType } from 'ui3/Card/ContentCard';
import InfiniteScrollLoader from 'ui3/InfiniteScrollLoader/InfiniteScrollLoader';
// Components
import InfiniteScroll from 'components/common3/InfiniteScroll/InfiniteScroll';
import styles from './TagArticles.module.scss';

const PER_PAGE = 12;

type TagArticlesProps = {
  tagName: string;
};

// Renders nothing for a tag no article carries, so the stores list stays the page's first block.
const TagArticles = ({ tagName }: TagArticlesProps): JSX.Element | null => {
  const input = {
    hashtagName: tagName,
    limit: PER_PAGE,
    direction: SortDirection.DESC,
  };

  const { data, fetchMore } = useQuery<
    GetArticlesByHashtag,
    GetArticlesByHashtagVariables
  >(GET_ARTICLES_BY_HASHTAG, {
    variables: { input },
    skip: !tagName,
  });

  const articles = data?.getArticlesByHashtag.items || [];
  const startAfter = data?.getArticlesByHashtag.startAfter;

  if (!articles.length) {
    return null;
  }

  const fetchMoreData = () =>
    fetchMore({ variables: { input: { ...input, startAfter } } });

  return (
    <section className={styles.root} aria-labelledby="tag-articles">
      <h2 id="tag-articles" className={styles.title}>
        Articles tagged #{tagName}
      </h2>
      <InfiniteScroll
        dataLength={articles.length}
        next={fetchMoreData}
        hasMore={!!startAfter}
        loader={<InfiniteScrollLoader />}
        isOverflowVisible
      >
        <ul className={styles.list}>
          {articles.map((article) => (
            <li key={article.id}>
              <ContentCard
                type={ContentType.NEWS}
                imageUrl={article.url || ''}
                tzCode={null}
                linkUrl={`/${article.store?.slug}/news/${article.slug}`}
                dateTime={article.createdAt || ''}
                title={article.title || ''}
                user={article.store?.storeDetails?.storeName || ''}
                userUrl={article.store?.slug ? `/${article.store.slug}` : '/'}
                isPlay={false}
                isArticleCard
              />
            </li>
          ))}
        </ul>
      </InfiniteScroll>
    </section>
  );
};

export default TagArticles;
